import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';


interface CartContextType {
  cart: {[key: string]: number};
  addToCart: (productId: string, products: any[]) => void;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number, products: any[]) => void;
  clearCart: () => void;
  getCartItemCount: () => number;
  getCartTotal: (products: any[]) => number;
  getCartItems: (products: any[]) => Array<{product: any, quantity: number}>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [cart, setCart] = useState<{[key: string]: number}>(() => {
    // Load cart from localStorage on initialization
    const savedCart = localStorage.getItem('bookfood_cart');
    const parsedCart = savedCart ? JSON.parse(savedCart) : {};
    console.log('🛒 CartContext: Initializing cart from localStorage:', parsedCart);
    return parsedCart;
  });

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('bookfood_cart', JSON.stringify(cart));
    console.log('🛒 CartContext: Cart saved to localStorage:', cart);
  }, [cart]);

  const addToCart = (productId: string, products: any[]) => {
    console.log('🛒 CartContext: Adding to cart - Product ID:', productId);
    console.log('🛒 CartContext: Available products:', products);
    
    const product = products.find(p => p.id === productId);
    console.log('🛒 CartContext: Found product:', product);
    
    if (!product) {
      console.error('🛒 CartContext: Product not found!');
      return;
    }
    
    setCart(prev => {
      const currentQuantity = prev[productId] || 0;
      const newQuantity = currentQuantity + 1;
      
      console.log('🛒 CartContext: Current quantity:', currentQuantity, 'New quantity:', newQuantity);
      
      // Check if adding one more would exceed stock
      if (newQuantity > product.stock) {
        console.log('🛒 CartContext: Stock exceeded!');
        alert(`Only ${product.stock} items available in stock`);
        return prev;
      }
      
      const newCart = {
        ...prev,
        [productId]: newQuantity
      };
      
      console.log('🛒 CartContext: New cart state:', newCart);
      return newCart;
    });
  };

  const removeFromCart = (productId: string) => {
    setCart(prev => {
      const newCart = { ...prev };
      if (newCart[productId] > 1) {
        newCart[productId] -= 1;
      } else {
        delete newCart[productId];
      }
      return newCart;
    });
  };

  const updateQuantity = (productId: string, quantity: number, products: any[]) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;
    
    setCart(prev => {
      if (quantity <= 0) {
        const newCart = { ...prev };
        delete newCart[productId];
        return newCart;
      }
      
      // Check if quantity exceeds stock
      if (quantity > product.stock) {
        alert(`Only ${product.stock} items available in stock`);
        return prev;
      }
      
      return {
        ...prev,
        [productId]: quantity
      };
    });
  };

  const clearCart = () => {
    console.log('🛒 CartContext: Clearing cart');
    setCart({});
    localStorage.removeItem('bookfood_cart');
  };

  const getCartItemCount = () => {
    return Object.values(cart).reduce((total, quantity) => total + quantity, 0);
  };

  const getCartTotal = (products: any[]) => {
    console.log('🛒 CartContext: Calculating cart total...');
    console.log('🛒 CartContext: Cart state:', cart);
    console.log('🛒 CartContext: Products:', products);
    console.log('🛒 CartContext: Products length:', products.length);
    
    if (!products || products.length === 0) {
      console.log('🛒 CartContext: No products available, returning 0');
      return 0;
    }
    
    const total = Object.entries(cart).reduce((total, [productId, quantity]) => {
      const product = products.find(p => p.id === productId);
      console.log(`🛒 CartContext: Product ${productId}:`, product, 'Quantity:', quantity);
      const itemTotal = product ? product.price * quantity : 0;
      console.log(`🛒 CartContext: Item total for ${productId}:`, itemTotal);
      return total + itemTotal;
    }, 0);
    
    console.log('🛒 CartContext: Final total:', total);
    return total;
  };

  const getCartItems = (products: any[]) => {
    return Object.entries(cart).map(([productId, quantity]) => {
      const product = products.find(p => p.id === productId);
      return { product, quantity };
    }).filter(item => item.product);
  };

  const value = {
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartItemCount,
    getCartTotal,
    getCartItems
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
